import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { IContactActual } from 'legacy/lut/classes/models/IContactActual'
import { IFormPartners } from 'legacy/lut/classes/models/IFormPartners'
import { IPartnerActualData } from 'legacy/lut/classes/models/IPartnerActualData'

import { IPartner } from 'clickNgo/entities/partner/models'
import { IDataProviderFilter, IDataProviderSort, Logic, Operator } from 'core-components/types'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import { CrudStore } from 'core/types/crudStore'
import { PartnerHelper } from 'onboarding/classes/helpers/PartnerHelper'
import { TMatchedPartnerExclusionFrontend } from 'onboarding/classes/models/partnerExclusion'
import { IResponseErrorPayload } from 'onboarding/types/response'
import { DateHelper } from 'onboarding/utils/dateHelper'
import {
  throwOnErrorWithoutPayload,
  throwOnInvalidRequestParams,
} from 'onboarding/utils/errorResponse/throwOnApiError'
import { extractFileNameFromContentDisposition } from 'onboarding/utils/headers'
import settings from 'src/config/settings'

export enum EPartnersCrud {
  requestDetails = 'requestDetails',
  partnerDataForm = 'partnerDataForm',
  partnerEditDialog = 'partnerEditDialog',
}

export interface ICrudOptions {
  crudKey?: EPartnersCrud
  sort?: IDataProviderSort | IDataProviderSort[]
  filter?: IDataProviderFilter
  needCached?: boolean
}

const CONTROLLER = 'partner'

export class OnboardingPartnerApi {
  public getDummyPartnerRequestsCrud(crudKey?: EPartnersCrud) {
    return CrudStore.get(CrudStoreItem.NewPartners, crudKey)
  }

  public getPartnersCrud(crudKey?: EPartnersCrud) {
    return CrudStore.get(CrudStoreItem.PartnerAllData, crudKey)
  }

  public fetchById(id: number, { crudKey }: ICrudOptions): Promise<IFormPartners | null> {
    return this.getPartnersCrud(crudKey).getById({ id, force: true, keyField: 'EntryID' })
  }

  public async fetchChangeRequestByPartnerId(
    partnerId: string,
    { crudKey, sort, filter, needCached }: ICrudOptions
  ): Promise<IFormPartners | null> {
    const source = this.getDummyPartnerRequestsCrud(crudKey)
    source.setDefaultFilter({
      logic: Logic.AND,
      filters: [{ field: 'PartnerID', operator: Operator.EQUAL, value: partnerId }],
    })

    const { items, error } = await source.fetch({
      skipHierarchyFilter: true,
      force: !needCached,
      skipTotal: true,
      filter,
      sort,
      paging: { page: 0, pageSize: 1 },
    })
    if (error) {
      throw new Error(error)
    }

    return items[0] ?? null
  }

  public async fetchOne({
    crudKey,
    sort,
    filter,
    needCached,
  }: ICrudOptions): Promise<IFormPartners | null> {
    const source = this.getPartnersCrud(crudKey)

    const { items, error } = await source.fetch({
      skipHierarchyFilter: true,
      force: !needCached,
      skipTotal: true,
      filter,
      sort,
      paging: { page: 0, pageSize: 1 },
    })
    if (error) {
      throw new Error(error)
    }

    return items[0] ?? null
  }

  public async fetchOneByPartnerId(
    partnerId: string,
    { crudKey, needCached }: Pick<ICrudOptions, 'crudKey' | 'needCached'>
  ): Promise<IFormPartners | null> {
    const filter: IDataProviderFilter = {
      logic: Logic.AND,
      filters: [{ field: 'PartnerID', operator: Operator.EQUAL, value: partnerId }],
    }

    return this.fetchOne({ crudKey, filter, needCached })
  }

  public fetchPartnerOrChangeRequest(
    partnerId: string,
    { crudKey, sort, filter, needCached }: ICrudOptions
  ): Promise<IFormPartners | null> {
    const source = PartnerHelper.isDummyPartnerRequest(partnerId)
      ? this.getDummyPartnerRequestsCrud(crudKey)
      : this.getPartnersCrud(crudKey)
    if (sort) {
      source.setSort(sort)
    }
    if (filter) {
      source.setFilter(filter)
    }

    return source.getById({ id: partnerId, force: !needCached, keyField: 'PartnerID' })
  }

  public async fetchPartnerMatchedExclusionList(
    partnerId: string
  ): Promise<TPartnerMatchedExclusionListResponse> {
    const response = await WebSocketStore.get(
      'onboarding'
    ).call<TPartnerMatchedExclusionListResponse>({
      service: CONTROLLER,
      action: 'fetch-matched-ams-exclusion-list',
      payload: { partnerId },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message)
    }

    return response.data
  }

  public async fetchOptionalFieldConfig(): Promise<TOptionalFieldConfig> {
    const response = await WebSocketStore.get('onboarding').call<TOptionalFieldConfig>({
      service: CONTROLLER,
      action: 'fetch-optional-field-config',
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message)
    }

    return response.data
  }

  public async checkAnyPartnerExistsWithTaxId(taxId: string): Promise<boolean> {
    const response = await WebSocketStore.get('onboarding').call<{ isPartnerExists: boolean }>({
      service: CONTROLLER,
      action: 'check-partner-by-tax-id-exists',
      payload: { taxId },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message)
    }

    return response.data.isPartnerExists
  }

  public async create(partner: IPartner): Promise<TCreatePartnerResponse> {
    const response = await WebSocketStore.get('onboarding').call<
      TCreatePartnerResponse & {
        errorPayload?: IResponseErrorPayload
        /** @deprecated use typed errorPayload */
        error?: any
      }
    >({
      service: CONTROLLER,
      action: 'create',
      payload: partner,
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      const defaultMessage = 'Failed to create partner'
      throwOnInvalidRequestParams(response)
      throwOnErrorWithoutPayload(response, defaultMessage)
    }

    return response.data
  }

  public async update(partner: IPartner): Promise<TUpdatePartnerResponse> {
    const response = await WebSocketStore.get('onboarding').call<
      TUpdatePartnerResponse & {
        errorPayload?: IResponseErrorPayload
        /** @deprecated use typed errorPayload */
        error?: any
      }
    >({
      service: CONTROLLER,
      action: 'update',
      payload: partner,
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      const defaultMessage = 'Failed to update partner'
      throwOnInvalidRequestParams(response)
      throwOnErrorWithoutPayload(response, defaultMessage)
    }

    return response.data
  }

  public async exportAll(accessToken: string): Promise<File> {
    const url = `${settings.httpsServices.onboarding}/api/v0/${CONTROLLER}/bpDormantReport/export`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        Authorization: accessToken,
      },
    })

    if (!response.ok) {
      const errorMessage = (await response.text()) || 'Failed to export'

      throw new Error(errorMessage)
    }

    const blob = await response.blob()

    return new File(
      [blob],
      extractFileNameFromContentDisposition(
        response,
        `${DateHelper.formatToFile(DateHelper.now())}_BP_Dormant_Report.xlsx`
      )
    )
  }
}

export const onboardingPartnerApi = new OnboardingPartnerApi()

export type TPartnerMatchedExclusionListResponse = {
  partner: IPartnerActualData
  contacts: IContactActual[]
  matches: TMatchedPartnerExclusionFrontend[]
}

/**
 * Страны и рынки, для которых сервис не требует Company URL (серверное правило `PartnerSchema.URL`).
 *
 * `countryWithOptionalField` — названия стран в нижнем регистре; сервис уже развернул в него
 * страны рынков из `marketWithOptionalField`, поэтому клиенту достаточно поля `Country`.
 * `marketWithOptionalField` нужен отдельно для партнёров из реестра, чей GeoMarket может
 * отличаться от рынка своей страны.
 */
export type TOptionalFieldConfig = {
  countryWithOptionalField: string[]
  marketWithOptionalField: string[]
}

export type TCreatePartnerResponse = {
  partnerId: string
  entryId406: number
  entryId502: number
  locationId: string
}

export type TUpdatePartnerResponse = Pick<
  IPartner,
  | 'entryId406'
  | 'entryId502'
  | 'workflowStatus'
  | 'legalWorkflowStatus'
  | 'modifiedValues'
  | 'legalModifiedValues'
  | 'partnerId'
  | 'locationId'
>
